import "@/utils/random-uuid-polyfill";
// Eval-free Pixi: swaps the `new Function` shader/UBO sync generators for
// polyfills so the CSP `script-src` needs no 'unsafe-eval'. Must run before any
// renderer is created. Requires pixi >= 8.20 — on 8.14 the UBO polyfill drops
// the batch-arena offset and blanks the WebGPU canvas
// (see docs/security/csp-report-only.md).
import "pixi.js/unsafe-eval";
import "@/i18n";
import * as Sentry from "@sentry/react";
import { ClerkLoaded, ClerkProvider, useAuth } from "@clerk/clerk-react";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import type { BaseAppComponents } from "@ue-too/board-pixi-integration";
import type { PixiCanvasRegistry } from "@ue-too/board-pixi-react-integration";
import { StrictMode, useEffect, useRef, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { useTranslation } from "react-i18next";

import { routeTree } from "../routeTree.gen";
import { clerkAppearance } from "@/config/clerk-appearance";
import { createClerkRouterFns } from "@/config/clerk-router";
import { getClerkLocalization } from "@/config/clerk-localization";
import { NotFound } from "@/components/not-found";
import { initCustomCursor } from "@/cursors/cursor-preference";
import { initBentoRadius } from "@/preferences/bento-radius-preference";
import { initCanvasColor } from "@/preferences/canvas-color-preference";
import { initFontSize } from "@/preferences/font-size-preference";
import { initTheme } from "@/preferences/theme-preference";
import { installScrollbarAutohide } from "@/utils/scrollbar-autohide";
import { AnalyticsProvider } from '@/analytics';
import { installBackendGates } from '@/analytics/backend-gates';
import { FeatureGateSync } from '@/analytics/feature-gate-sync';
import { PlanProvider } from '@/billing/plan-context';
import { UpgradeProvider } from '@/billing/upgrade-context';
import { ConsentProvider, useConsent } from '@/consent';
import { isErrorMonitoringActive, readConsent } from '@/consent/store';
import { dismissSplashOnShellReady } from "@/utils/splash";
import { setAuthTokenProvider } from "@/diagram-persistence/store-instance";
import { useClaimTryDiagrams } from "@/pages/try-editor/use-claim-try-diagrams";
import { initSentry } from "./instrument";
import type { Grid } from "./grid/grid";
import type { PixiGrid } from "./grid/grid-pixi";
import type { GridHistoryController } from "./grid/grid-history-controller";
import type { HistoryManager } from "./history";
import type { GridToolStateMachine } from "./utils/input-state-machine/grid-tool-state-machine";
import type { GridModeStateMachine, GridModeStates } from "./utils/input-state-machine/grid-mode-state-machine";
import type { ExpandedInputTracker } from "./utils/input-state-machine/kmt-input-state-machine-expansion";
import type { TbcEngine } from "./crochet-editor/tbc-engine";
import type { TbcPixiLayer } from "./crochet-editor/tbc-pixi";
import type { CrochetKmtStateMachine } from "./crochet-editor/input/crochet-kmt-expansion";
import type { CrochetHistoryContext, CrochetHistoryOperation } from "./crochet-editor/crochet-history";
import type { PieMenuController } from "./utils/pie-menu/pie-menu-controller";
import type { RoundView } from "@/crochet-editor/round-view";
import type { OrderView } from "@/crochet-editor/order-view";
import type { VerticalLinkView } from "@/crochet-editor/vertical-link-view";
import type { GridBackdrop } from "@/canvas-backdrop/grid-backdrop";
import type { OriginMarker } from "@/crochet-editor/origin-marker";
import type { CanvasExtentHandle } from "@/canvas-extent/install-canvas-extent";
import type { StitchLabelOverlay } from "@/crochet-editor/stitch-label-overlay";
import type { OrderPickSession } from "@/crochet-editor/order-pick-session";
import type { OrderPickStateMachine } from "@/crochet-editor/input/crochet-order-pick-state-machine";
import type { EditorLayer, EditorLayerStateMachine } from "@/annotation/editor-layer-state-machine";
import type { AnnotationView } from "@/annotation/annotation-view";
import type { CrochetQuickAccessSurface } from "@/quick-access/quick-access-types";
import "./style.css";

export type AppComponents = BaseAppComponents & {
  type: "grid";
  grid: Grid;
  pixiGrid: PixiGrid;
  inputTracker: ExpandedInputTracker;
  gridToolStateMachine: GridToolStateMachine;
  gridModeStateMachine: GridModeStateMachine;
  onGridModeChange: (cb: (mode: GridModeStates) => void) => () => void;
  gridHistory: GridHistoryController;
  pieMenuController: PieMenuController;
  gridBackdrop: GridBackdrop;
  /** Auto-growing pan area (src/canvas-extent/): its edge marker is hidden
   *  for export, and the embed flushes it before fitting the camera. */
  canvasExtent: CanvasExtentHandle;
  clientToWorld: (clientX: number, clientY: number) => { x: number; y: number };
  worldToClient: (worldX: number, worldY: number) => { x: number; y: number };
  /** `null` in view mode: no layer switching for viewers. */
  editorLayerStateMachine: EditorLayerStateMachine | null;
  onEditorLayerChange: (cb: (layer: EditorLayer) => void) => () => void;
};

export type CrochetAppComponents = BaseAppComponents & {
  type: "crochet";
  canvasElement: HTMLCanvasElement;
  engine: TbcEngine;
  roundView: RoundView;
  orderView: OrderView;
  verticalLinkView: VerticalLinkView;
  pixiLayer: TbcPixiLayer;
  annotationView: AnnotationView;
  inputStateMachine: CrochetKmtStateMachine;
  history: HistoryManager<CrochetHistoryContext, CrochetHistoryOperation>;
  pieMenuController: PieMenuController;
  clientToWorld: (clientX: number, clientY: number) => { x: number; y: number };
  worldToClient: (worldX: number, worldY: number) => { x: number; y: number };
  gridBackdrop: GridBackdrop;
  originMarker: OriginMarker;
  /** Auto-growing pan area (src/canvas-extent/): its edge marker is hidden
   *  for export, and the embed flushes it before fitting the camera. */
  canvasExtent: CanvasExtentHandle;
  labelOverlay: StitchLabelOverlay;
  /** Order mode's data (SP-48). `null` in view mode: viewers cannot number. */
  orderPickSession: OrderPickSession | null;
  /** Order mode's state machine (SP-48): `happens('open')` enters it. `null`
   *  in view mode. */
  orderPick: OrderPickStateMachine | null;
  /** `null` in view mode: no layer switching for viewers. */
  editorLayerStateMachine: EditorLayerStateMachine | null;
  onEditorLayerChange: (cb: (layer: EditorLayer) => void) => () => void;
  /** Opens the `being` state-machine debugger over this editor's input
   *  machines. `null` in view mode; a no-op in production builds. */
  openDevtools: (() => void) | null;
  /** Quick Access (W menu) hooks into the input stack. `null` in view mode. */
  quickAccess: CrochetQuickAccessSurface | null;
  cleanup?: () => void;
};

declare module "@ue-too/board-pixi-react-integration" {
  interface PixiCanvasRegistry {
    components: AppComponents | CrochetAppComponents;
  }
}

/**
 * Router instance. The route tree is generated from routes/ by @tanstack/router-plugin.
 * The `auth` context is injected at render time from Clerk via <InnerApp />.
 */
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultNotFoundComponent: () => <NotFound />,
  context: {
    auth: undefined!,
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Sentry is gated on its own error-monitoring consent category (separate from
// Statsig analytics). Returning users who already granted it get Sentry up
// before first paint so early errors are captured; first-time grants (which
// flip state without a reload) are handled reactively by <SentryConsentGate />.
// initSentry is idempotent, so the two paths never double-initialize.
if (isErrorMonitoringActive(readConsent())) {
  initSentry(router);
}

export type ClerkAuthContext = ReturnType<typeof useAuth>;

// In-component Clerk navigations (sign-in steps) go through the router so
// they don't full-reload the app; everything else stays a window navigation.
// See src/config/clerk-router.ts for why.
const clerkRouter = createClerkRouterFns(({ href, replace }) => router.navigate({ href, replace }));

/**
 * Brings Sentry up the moment the user grants analytics consent without a page
 * reload (the banner's accept path just flips React state). No-op once Sentry
 * is already running.
 */
function SentryConsentGate() {
  const { consent } = useConsent();
  useEffect(() => {
    if (isErrorMonitoringActive(consent)) initSentry(router);
  }, [consent]);
  return null;
}

function InnerApp() {
  const auth = useAuth();
  // Editor routes keep the splash up until Pixi init settles (see
  // <SplashDismisser />) — with a navigation watch so a beforeLoad redirect
  // (e.g. signed-out → /sign-in) can't strand the splash; everything else is
  // ready once the shell mounts.
  useEffect(
    () => dismissSplashOnShellReady(window.location.pathname, router),
    [],
  );
  const getTokenRef = useRef(auth.getToken);
  getTokenRef.current = auth.getToken;
  useEffect(() => {
    // Signed-in → diagram saves sync to the account; otherwise local-only.
    // Keyed on userId too (not just isSignedIn) so an account switch on a
    // shared browser re-registers the identity — the new userId flows into
    // store-instance's ownership check, which wipes the stale local cache
    // before the new account's diagrams sync in (see cache-ownership.ts).
    // InnerApp never unmounts, so there's no cleanup to run here — the next
    // run of this effect (on the next isSignedIn/userId change) is the only
    // teardown this needs.
    setAuthTokenProvider(
      auth.isSignedIn ? { getToken: () => getTokenRef.current(), userId: auth.userId } : null,
    );
  }, [auth.isSignedIn, auth.userId]);
  // Claims any /try work into the account the moment it's synced. Declared
  // AFTER the effect above on purpose — see useClaimTryDiagrams' docstring
  // for why the ordering matters (it must run once setAuthTokenProvider has
  // registered the identity, or getDiagramStore() still returns the
  // pre-sign-in local-only store).
  useClaimTryDiagrams(auth.isSignedIn, auth.userId);
  // Plan state sits outside the router so every route (and the global chrome)
  // reads one shared answer from usePlan(). The upgrade dialogs sit just inside
  // it, outside the router, so any route can open them. The offline entry
  // mounts neither.
  return (
    <PlanProvider>
      <UpgradeProvider>
        <RouterProvider router={router} context={{ auth }} />
      </UpgradeProvider>
    </PlanProvider>
  );
}

/**
 * Wraps <ClerkProvider> so its `localization` follows the app's current
 * i18next language. Reading `i18n.language` via useTranslation re-renders this
 * component on language change, updating the localization in place (no remount).
 */
function LocalizedClerkProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      afterSignOutUrl="/sign-in"
      waitlistUrl="/waitlist"
      routerPush={clerkRouter.routerPush}
      routerReplace={clerkRouter.routerReplace}
      appearance={clerkAppearance}
      localization={getClerkLocalization(i18n.language)}
    >
      {children}
    </ClerkProvider>
  );
}

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY environment variable.");
}

/**
 * React application entry point.
 * Mounts the router to the root element.
 */
const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error(
    'Root element not found. Make sure there is a <div id="root"></div> in your HTML.'
  );
}

// Apply the persisted custom-cursor, font-size and bento-radius preferences
// and the cached theme to <html> before first paint.
initCustomCursor();
initFontSize();
initBentoRadius();
initTheme();
// Every gate read comes from the backend's GET /api/gates, for every user;
// <FeatureGateSync> seeds this client from its cache and fetches once Clerk
// has loaded.
const backendGates = installBackendGates();
initCanvasColor();
installScrollbarAutohide();

const root = createRoot(rootElement, {
  // React 19 surfaces render/runtime errors through these callbacks; forward
  // them to Sentry. reactErrorHandler() no-ops safely while Sentry is dormant
  // (consent not yet granted).
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(
  <StrictMode>
    <ConsentProvider>
      <SentryConsentGate />
      <LocalizedClerkProvider>
        {/* While Clerk loads, the static index.html splash is still covering
            the viewport — no in-React fallback needed. */}
        <ClerkLoaded>
          <FeatureGateSync client={backendGates} />
          <AnalyticsProvider>
            <InnerApp />
          </AnalyticsProvider>
        </ClerkLoaded>
      </LocalizedClerkProvider>
    </ConsentProvider>
  </StrictMode>
);
